/**
 * Отрисовка текущего view: узлы и связи на графе.
 * Конфиг маркеров рёбер — в edgeConfig; создание edge — через EdgeFactory при наличии.
 */
import {toRgbaString} from '../draw/ArchimateRendererUtil';
import {JUNCTION, AND_JUNCTION, OR_JUNCTION, buildX6RouterConfig} from './constants';
import * as viewHelpers from './viewHelpers';
import {getEdgeMarkerConfigForRelationshipType} from './edgeConfig';
import {detachEdgeFromNodeParent} from './edgeKeepRoot';

export function resolveView(modeler, viewOrId) {
	const views = modeler.getViews();
	if (!views.length) return null;
	if (!viewOrId) return views[0];
	if (typeof viewOrId === 'string') {
		return views.find(view => view.id === viewOrId) || null;
	}
	return viewOrId;
}

export function renderView(modeler, view) {
	modeler._currentView = view;
	modeler._addingConnections = true;
	const views = modeler.getViews();
	modeler._currentViewIndex = views.findIndex(
		v => (v?.id ?? v?.$attrs?.id) === (view?.id ?? view?.$attrs?.id)
	);
	if (modeler._currentViewIndex < 0) modeler._currentViewIndex = 0;
	const graph = modeler.getGraph();
	if (!graph) throw new Error('Graph is not initialized');
	modeler._closeLabelEditor?.();
	/** Снять selection / transform / tools до clearCells — иначе после redo остаётся «призрак» на основном холсте, а миникарта по модели уже без узла. */
	try {
		graph.cleanSelection?.();
		graph.clearTransformWidgets?.();
		graph.getCells?.()?.forEach(c => {
			try {
				c.removeTools?.();
			} catch (_) {
				// ignore
			}
		});
	} catch (_) {
		// ignore
	}
	/** Не слать updateConnection из edgeTerminal/edgeVertices пока граф и роутер не устаканятся после bulk-рендера (иначе ложные патчи при HMR/смене view). */
	modeler._suppressConnectionPatchSync = true;
	const history = graph.getPlugin?.('history');
	history?.disable?.();

	try {
		/* clearCells эмитит node:removed / edge:removed — без флага плагины шлют ложные remove* в сокет (баг при importJSON/undo). */
		modeler._applyingPatch = true;
		// Очищаем граф (без silent, чтобы view обновился). Патчи при рендере не уходят:
		// node:added-to-view не эмитится, edge:added обрабатывается только при _fromUserConnection.
		graph.clearCells?.();
		modeler._graphAddOptions = {fromRender: true};

		const nodesToRender = collectAllNodeViewElements(view);
		const addedNodeIds = new Set();
		// Проход 1: добавляем все узлы на граф (без привязки к родителю, чтобы родитель уже существовал)
		nodesToRender.forEach(({viewElement, parentViewElement}) => {
			const nodeId = viewElement?.id ?? viewElement?.$attrs?.id;
			if (!nodeId) return;
			if (addedNodeIds.has(nodeId)) return;
			addedNodeIds.add(nodeId);

			const type = viewHelpers.getType(modeler, viewElement);
			const isJunction = [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(type);
			if (isJunction) {
				viewElement.w = 20;
				viewElement.h = 20;
				if (viewElement.bounds) {
					viewElement.bounds.width = 20;
					viewElement.bounds.height = 20;
				}
			}
			const bounds = viewHelpers.getBounds(modeler, viewElement);
			const label = viewHelpers.getLabel(modeler, viewElement);

			if (!viewHelpers.isRenderableElement(modeler, type)) return;

			modeler.createElementNode(type, {
				id: nodeId,
				x: bounds.x,
				y: bounds.y,
				width: bounds.width,
				height: bounds.height,
				label,
				parent: undefined,
				viewElement,
			});
		});
		// Проход 2: привязываем детей к родителям (parent.addChild), когда все ячейки уже на графе
		nodesToRender.forEach(({viewElement, parentViewElement}) => {
			const nodeId = viewElement?.id ?? viewElement?.$attrs?.id;
			if (!nodeId || !parentViewElement) return;
			const parentId = parentViewElement.id ?? parentViewElement.$attrs?.id;
			if (!parentId || parentId === '') return;
			const cell = graph.getCellById?.(nodeId) ?? graph.model?.getCellById?.(nodeId);
			const parentCell =
				graph.getCellById?.(parentId) ?? graph.model?.getCellById?.(parentId);
			if (parentCell && cell && typeof parentCell.addChild === 'function') {
				parentCell.addChild(cell);
				modeler.bringCellToFront?.(parentId);
			}
		});

		const connectionsToRender = collectAllConnectionViewElements(modeler, view);
		connectionsToRender.forEach(connectionViewElement => {
			const sourceId =
				getConnectionEndpointId(connectionViewElement.source) ??
				connectionViewElement.$attrs?.source;
			const targetId =
				getConnectionEndpointId(connectionViewElement.target) ??
				connectionViewElement.$attrs?.target;
			if (
				!sourceId ||
				!targetId ||
				!addedNodeIds.has(sourceId) ||
				!addedNodeIds.has(targetId)
			) {
				return;
			}
			const waypoints = getConnectionWaypoints(connectionViewElement);
			const relationshipType = getRelationshipType(modeler, connectionViewElement);
			const accessType =
				relationshipType === 'Access'
					? getRelationshipAccessType(modeler, connectionViewElement)
					: undefined;
			const connStyle = connectionViewElement?.style;
			const connLineColor =
				connStyle && (connStyle.lineColor ?? connStyle.get?.('lineColor'));
			const relRef =
				connectionViewElement?.relationshipRef ??
				connectionViewElement?.$attrs?.relationshipRef;
			const relName =
				relRef &&
				(typeof relRef === 'object' ? (relRef.name ?? relRef.get?.('name')) : null);
			const connectionLabel =
				connectionViewElement?.label != null && connectionViewElement.label !== ''
					? String(connectionViewElement.label)
					: connectionViewElement?.$attrs?.label != null &&
						  connectionViewElement.$attrs.label !== ''
						? String(connectionViewElement.$attrs.label)
						: relName != null && relName !== ''
							? String(relName)
							: '';
			if (modeler._edgeFactory) {
				modeler._edgeFactory.createAndAdd({
					id: connectionViewElement.id,
					source: sourceId,
					target: targetId,
					vertices: waypoints,
					relationshipType,
					accessType,
					lineColor: connLineColor ? toRgbaString(connLineColor) : undefined,
					label:
						typeof connectionLabel === 'string'
							? connectionLabel
							: String(connectionLabel ?? ''),
				});
			} else {
				addConnectionEdge(modeler, {
					id: connectionViewElement.id,
					source: sourceId,
					target: targetId,
					vertices: waypoints,
					relationshipType,
					accessType,
					lineColor: connLineColor ? toRgbaString(connLineColor) : undefined,
					label:
						typeof connectionLabel === 'string'
							? connectionLabel
							: String(connectionLabel ?? ''),
				});
			}
		});
		// До setupPatchSync: снимаем embedding со всех рёбер здесь, а не в microtask init плагина
		// (иначе при каждом перезапуске setupPatchSync снова detach → ложные updateConnection в сокет).
		graph.getEdges?.()?.forEach(edge => detachEdgeFromNodeParent(edge));
		modeler._graphAddOptions = undefined;
		modeler._addingConnections = false;
		if (modeler._nodeLastPosition) {
			graph.getNodes?.()?.forEach(n => {
				const p = n.getPosition?.();
				if (p) modeler._nodeLastPosition.set(n.id, {x: p.x, y: p.y});
			});
		}
		try {
			graph.cleanSelection?.();
			graph.clearTransformWidgets?.();
			graph.getCells?.()?.forEach(c => {
				try {
					c.removeTools?.();
				} catch (_) {
					// ignore
				}
			});
		} catch (_) {
			// ignore
		}
	} finally {
		modeler._applyingPatch = false;
		const afterBoardReady = () => {
			try {
				if (typeof graph.cleanHistory === 'function') {
					graph.cleanHistory();
				}
			} catch (_) {}
			history?.enable?.();
			// терминалы могут прийти в следующем кадре после enable history — не слать ложные updateConnection
			const RELEASE_MS = 500;
			setTimeout(() => {
				modeler._suppressConnectionPatchSync = false;
			}, RELEASE_MS);
		};
		if (typeof requestAnimationFrame === 'function') {
			requestAnimationFrame(afterBoardReady);
		} else {
			afterBoardReady();
		}
	}
}

export function collectAllNodeViewElements(view, parentViewElement = null) {
	const result = [];
	const elements = view?.viewElements || [];
	for (const el of elements) {
		if (el.waypoints) continue;
		result.push({viewElement: el, parentViewElement});
		if (el.viewElements && el.viewElements.length > 0) {
			result.push(...collectAllNodeViewElements({viewElements: el.viewElements}, el));
		}
	}
	return result;
}

export function collectAllConnectionViewElements(modeler, view) {
	const result = [];
	const elements = view?.viewElements || [];
	for (const el of elements) {
		if (isConnectionViewElement(el)) result.push(el);
		if (el.viewElements && el.viewElements.length > 0) {
			result.push(
				...collectAllConnectionViewElements(modeler, {viewElements: el.viewElements})
			);
		}
	}
	return result;
}

export function isConnectionViewElement(el) {
	return Boolean(
		el.relationshipRef ||
		(el.source && el.target) ||
		(el.$attrs && (el.$attrs.relationshipRef || (el.$attrs.source && el.$attrs.target)))
	);
}

export function getConnectionEndpointId(sourceOrTarget) {
	if (!sourceOrTarget) return null;
	if (typeof sourceOrTarget === 'string') return sourceOrTarget;
	return sourceOrTarget.id ?? sourceOrTarget.$attrs?.id ?? null;
}

export function getConnectionWaypoints(connectionViewElement) {
	const waypointsNode = connectionViewElement.waypointsNode || connectionViewElement.waypoints;
	const list = waypointsNode?.waypoints || waypointsNode?.waypoint || [];
	const arr = Array.isArray(list) ? list : [list];
	return arr.filter(Boolean).map(wp => ({
		x: Math.round(Number(wp.x ?? wp.$attrs?.x ?? 0)),
		y: Math.round(Number(wp.y ?? wp.$attrs?.y ?? 0)),
	}));
}

function connectionTypeFallback(connectionViewElement) {
	const raw =
		connectionViewElement?.type ??
		connectionViewElement?.$type ??
		connectionViewElement?.$attrs?.type ??
		connectionViewElement?.$attrs?.['xsi:type'];
	const normalized = viewHelpers.normalizeType(raw || '');
	if (!normalized || normalized === 'Line' || normalized === 'Relationship') return null;
	return normalized;
}

export function getRelationshipType(modeler, connectionViewElement) {
	let rel = connectionViewElement.relationshipRef;
	if (!rel) return connectionTypeFallback(connectionViewElement);
	if (typeof rel === 'string') {
		const rels =
			modeler._model?.relationshipsNode?.relationship ||
			modeler._model?.relationships?.relationship ||
			[];
		const list = Array.isArray(rels) ? rels : [rels];
		rel =
			list.find(
				r => (r.id ?? r.$attrs?.id ?? r.identifier ?? r.$attrs?.identifier) === rel
			) || null;
	}
	if (!rel) return connectionTypeFallback(connectionViewElement);
	const raw =
		rel.type ??
		rel.$type ??
		rel.$attrs?.['type'] ??
		rel.$attrs?.['xsi:type'] ??
		connectionViewElement.type ??
		connectionViewElement.$type ??
		connectionViewElement.$attrs?.['type'] ??
		connectionViewElement.$attrs?.['xsi:type'];
	const normalized = viewHelpers.normalizeType(raw || '');
	return normalized || connectionTypeFallback(connectionViewElement);
}

export function getRelationshipAccessType(modeler, connectionViewElement) {
	let rel = connectionViewElement.relationshipRef;
	if (!rel) return null;
	if (typeof rel === 'string') {
		const rels =
			modeler._model?.relationshipsNode?.relationship ||
			modeler._model?.relationships?.relationship ||
			[];
		const list = Array.isArray(rels) ? rels : [rels];
		rel =
			list.find(
				r => (r.id ?? r.$attrs?.id ?? r.identifier ?? r.$attrs?.identifier) === rel
			) || null;
	}
	if (!rel) return null;
	return rel.accessType ?? rel.$attrs?.accessType ?? null;
}

/**
 * Добавить ребро на граф. Если у modeler есть _edgeFactory — делегирует ему, иначе строит edge вручную (fallback).
 */
export function addConnectionEdge(
	modeler,
	{id, source, target, vertices = [], relationshipType, accessType, lineColor, label} = {}
) {
	if (modeler._edgeFactory) {
		return modeler._edgeFactory.createAndAdd({
			id,
			source,
			target,
			vertices,
			relationshipType,
			accessType,
			lineColor,
			label,
		});
	}
	const graph = modeler.getGraph();
	if (!graph) throw new Error('Graph is not initialized');
	const markerConfig = getEdgeMarkerConfigForRelationshipType(relationshipType, {accessType});
	const lineAttrs = {
		stroke: lineColor ?? '#000000',
		strokeWidth: 1,
	};
	if (markerConfig.sourceMarker != null) lineAttrs.sourceMarker = markerConfig.sourceMarker;
	if (markerConfig.targetMarker != null) lineAttrs.targetMarker = markerConfig.targetMarker;
	if (markerConfig.strokeDasharray != null)
		lineAttrs.strokeDasharray = markerConfig.strokeDasharray;
	const routerName = modeler?.getConnectionRouter?.() ?? 'normal';
	const edgeAttrs = {line: lineAttrs};
	const edgeOptions = {
		id,
		zIndex: 10000,
		source: {cell: source, anchor: 'orth', connectionPoint: 'boundary'},
		target: {cell: target, anchor: 'orth', connectionPoint: 'boundary'},
		vertices: vertices.length ? vertices : undefined,
		attrs: edgeAttrs,
		connector: {name: 'jumpover', args: {size: 6, type: 'arc'}},
		router: buildX6RouterConfig(routerName),
		label: typeof label === 'string' ? label : (label ?? ''),
	};
	return graph.addEdge(edgeOptions);
}

export {getEdgeMarkerConfigForRelationshipType} from './edgeConfig';
